/* eslint-disable @next/next/no-img-element -- srcset/sizes repris tels quels de la maquette YES-69 */
import { Fragment, type CSSProperties, type ReactNode } from 'react'

// Styles partagés du site getyesman.com. Les valeurs viennent de la maquette YES-69 :
// ne pas les arrondir ni les convertir en classes Tailwind, le rendu doit rester identique.

export const sectionInner: CSSProperties = {
  padding: 'clamp(80px, 9vw, 144px) clamp(20px, 5.5vw, 80px)',
  maxWidth: '1600px',
  margin: '0 auto',
  boxSizing: 'border-box',
}

export const twoColumns: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: 'clamp(40px, 6vw, 96px)',
}

export const sectionTitle: CSSProperties = {
  margin: '0',
  fontSize: 'clamp(30px, 3.8vw, 60px)',
  lineHeight: '1.06',
  letterSpacing: '-0.025em',
  fontWeight: 800,
  fontStretch: '118%',
}

export const sectionBody: CSSProperties = {
  margin: '0',
  fontSize: 'clamp(18px, 1.4vw, 21px)',
  lineHeight: '1.6',
  color: '#3A4048',
  maxWidth: '46ch',
}

export const textColumn: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '32px',
}

export const coverImage: CSSProperties = {
  position: 'absolute',
  inset: '0',
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
}

export const overlay: CSSProperties = {
  position: 'absolute',
  inset: '0',
}

export function YesManLogo({ size, style }: { size: number; style?: CSSProperties }) {
  return (
    <svg viewBox="20 20 472 472" width={size} height={size} aria-hidden="true" style={{ display: 'block', flex: 'none', ...style }}>
      <rect x="22" y="22" width="468" height="468" rx="96" fill="#E4F1F6"></rect>
      <path
        d="M256.9,245.5L394.1,143.1L394.1,90.0L256.9,195.7L119.7,90.0L119.8,143.1ZM236.7,425.5L238.5,276.1L119.8,183.4L119.7,358.1L155.5,375.0L156.0,259.6L158.8,257.5L200.0,295.1L200.5,401.9ZM313.4,401.9L313.9,295.1L355.1,257.5L357.9,259.6L358.4,375.0L394.2,358.1L394.1,183.4L275.4,276.1L277.2,425.5Z"
        fill="#006E9E"
      ></path>
    </svg>
  )
}

interface ResponsiveImageProps {
  /** Nom de base dans `public/images/`, sans le suffixe `-<largeur>.webp`. */
  name: string
  widths: [number, number]
  width: number
  height: number
  alt: string
  sizes: string
  className?: string
  style?: CSSProperties
  priority?: boolean
  hidden?: boolean
}

export function ResponsiveImage({ name, widths, width, height, alt, sizes, className, style, priority, hidden }: ResponsiveImageProps) {
  const [small, large] = widths
  return (
    <img
      className={className}
      src={`/images/${name}-${large}.webp`}
      srcSet={`/images/${name}-${small}.webp ${small}w, /images/${name}-${large}.webp ${large}w`}
      sizes={sizes}
      width={width}
      height={height}
      fetchPriority={priority ? 'high' : undefined}
      loading={priority ? undefined : 'lazy'}
      decoding="async"
      alt={alt}
      aria-hidden={hidden === undefined ? undefined : hidden}
      style={style}
    />
  )
}

/** Message de YesMan affiché en haut des photos de preuve. */
export function ProofMessage({ lines, style, textStyle }: { lines: string[]; style: CSSProperties; textStyle: CSSProperties }) {
  return (
    <div style={{ position: 'absolute', ...style, display: 'flex', flexDirection: 'column', gap: '8px', color: '#FFFFFF' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <YesManLogo size={24} />
        <span style={{ fontSize: 'clamp(14px, 1.1vw, 16px)', fontWeight: 600 }}>YesMan</span>
      </div>
      <p style={{ margin: '0', lineHeight: '1.22', letterSpacing: '-0.01em', fontWeight: 600, ...textStyle }}>
        {lines.map((line, index) => (
          <Fragment key={line}>
            {index > 0 && <br />}
            {line}
          </Fragment>
        ))}
      </p>
    </div>
  )
}

export type TypingClass = 'typed' | 'typed2' | 'typed3' | 'typed4' | 'typed5'

const replyCardSizes = {
  sm: { padding: '10px 14px 12px', boxShadow: '0 12px 32px rgba(11,13,16,0.22)', gap: '5px', fontSize: 'clamp(15px, 1.2vw, 18px)' },
  md: { padding: '12px 16px 14px', boxShadow: '0 16px 40px rgba(11,13,16,0.25)', gap: '6px', fontSize: 'clamp(16px, 1.3vw, 20px)' },
  lg: { padding: '14px 18px 16px', boxShadow: '0 16px 40px rgba(11,13,16,0.25)', gap: '6px', fontSize: 'clamp(18px, 1.6vw, 24px)' },
} as const

interface ReplyCardProps {
  name: string
  text: string
  /** Largeur minimale réservée au texte (dépend de la langue, cf. maquette). */
  minWidth: string
  size: keyof typeof replyCardSizes
  typing?: TypingClass
  style?: CSSProperties
}

/** Réponse d'un membre de l'équipe, avec animation de frappe optionnelle. */
export function ReplyCard({ name, text, minWidth, size, typing, style }: ReplyCardProps) {
  const { padding, boxShadow, gap, fontSize } = replyCardSizes[size]
  let content: ReactNode = text
  if (typing) {
    content = (
      <>
        <span className={typing} style={{ display: 'inline-block', overflow: 'hidden', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
          {text}
        </span>
        <span
          className="caret"
          aria-hidden="true"
          style={{ display: 'inline-block', width: '2px', height: '1.1em', marginLeft: '3px', background: '#006E9E' }}
        ></span>
      </>
    )
  }

  return (
    <div style={{ ...style, padding, borderRadius: '14px', background: '#E4F1F6', boxShadow, display: 'flex', flexDirection: 'column', gap }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ display: 'block', width: '8px', height: '8px', background: '#006E9E' }}></span>
        <span style={{ fontSize: '13px', fontWeight: 600, color: '#3A4048' }}>{name}</span>
      </div>
      <p style={{ margin: '0', minWidth, display: 'flex', alignItems: 'center', fontSize, fontWeight: 600, color: '#0B0D10', whiteSpace: 'nowrap' }}>
        {content}
      </p>
    </div>
  )
}
